<template>
  <div class="pickup-page">
    <!-- 顶部导航栏 -->
    <div class="nav-bar">
      <span class="nav-back" @click="$router.back()">
        <i class="el-icon-arrow-left"></i>
      </span>
      <span class="nav-title">提车</span>
      <span class="nav-submit" :class="{ disabled: submitting }" @click="handleSubmit">
        {{ submitting ? '提交中...' : '提交' }}
      </span>
    </div>

    <!-- 内容区 -->
    <div class="content">
      <!-- 顶部提示 -->
      <div class="tip-banner">
        为保障代驾/运输服务全程可追溯，请您拍摄并上传车辆照片（需带时间和地点水印）
      </div>
      <!-- 第一组：证件照片 -->
      <div class="section">
        <div class="photo-grid">
          <div class="photo-item" v-for="item in requiredPhotos" :key="item.key">
            <div class="photo-box" @click="handlePhotoClick(item)">
              <!-- 上传中 -->
              <div v-if="item.loading" class="photo-loading">
                <i class="el-icon-loading"></i>
                <span>上传中...</span>
              </div>
              <!-- 已上传预览 -->
              <template v-else-if="item.url">
                <img class="photo-img" :src="item.url" @click.stop="previewImage(item)" />
                <div class="photo-overlay" @click.stop="previewImage(item)">
                  <i class="el-icon-zoom-in"></i>
                </div>
                <span class="photo-delete" @click.stop="removePhoto(item)">
                  <i class="el-icon-delete"></i>
                </span>
              </template>
              <!-- 空状态 -->
              <template v-else>
                <svg class="camera-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="14" width="36" height="26" rx="4" stroke="#4a90e2" stroke-width="2.5"/>
                  <path d="M16 14 L18 8 L30 8 L32 14" stroke="#4a90e2" stroke-width="2.5" stroke-linejoin="round"/>
                  <circle cx="24" cy="27" r="7" stroke="#4a90e2" stroke-width="2.5"/>
                </svg>
              </template>
            </div>
            <span class="photo-label">{{ item.label }}<span class="required">*</span></span>
          </div>
        </div>
      </div>

      <!-- 信息录入 -->
      <div class="form-section">
        <div class="form-item">
          <span class="form-label">提车公里数<span class="required">*</span></span>
          <div class="form-input-wrap">
            <input v-model="formData.start_mileage" type="number" class="form-input" placeholder="请输入..." />
            <span class="input-suffix">km</span>
          </div>
        </div>
        <div class="form-item form-item-textarea">
          <span class="form-label">备注</span>
          <textarea v-model="formData.content" class="form-textarea" placeholder="请输入备注信息..." rows="3"></textarea>
        </div>
      </div>

      <!-- 第二组：车辆检查照片 -->
      <div class="section">
        <div class="photo-grid">
          <div class="photo-item" v-for="item in vehiclePhotos" :key="item.key">
            <div class="photo-box" @click="handlePhotoClick(item)">
              <div v-if="item.loading" class="photo-loading">
                <i class="el-icon-loading"></i>
                <span>上传中...</span>
              </div>
              <template v-else-if="item.url">
                <img class="photo-img" :src="item.url" @click.stop="previewImage(item)" />
                <div class="photo-overlay" @click.stop="previewImage(item)">
                  <i class="el-icon-zoom-in"></i>
                </div>
                <span class="photo-delete" @click.stop="removePhoto(item)">
                  <i class="el-icon-delete"></i>
                </span>
              </template>
              <template v-else>
                <svg class="camera-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="14" width="36" height="26" rx="4" stroke="#4a90e2" stroke-width="2.5"/>
                  <path d="M16 14 L18 8 L30 8 L32 14" stroke="#4a90e2" stroke-width="2.5" stroke-linejoin="round"/>
                  <circle cx="24" cy="27" r="7" stroke="#4a90e2" stroke-width="2.5"/>
                </svg>
              </template>
            </div>
            <span class="photo-label">{{ item.label }}</span>
          </div>
        </div>
      </div>

      <!-- 更多照片 -->
      <div class="section">
        <div class="more-photos">
          <div style="display: flex;"><span class="more-label">更多照片</span><span class="more-tip">(补充上传其余照片)</span></div>
          <div class="more-grid">
            <div class="photo-item more-item" v-for="(photo, index) in morePhotos" :key="'more-' + index">
              <div class="photo-box" @click="handleMorePhotoClick(index)">
                <div v-if="photo.loading" class="photo-loading">
                  <i class="el-icon-loading"></i>
                  <span>上传中...</span>
                </div>
                <template v-else-if="photo.url">
                  <img class="photo-img" :src="photo.url" @click.stop="previewImage(photo)" />
                  <div class="photo-overlay" @click.stop="previewImage(photo)">
                    <i class="el-icon-zoom-in"></i>
                  </div>
                  <span class="photo-delete" @click.stop="removeMorePhoto(index)">
                    <i class="el-icon-delete"></i>
                  </span>
                </template>
                <template v-else>
                  <svg class="camera-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="14" width="36" height="26" rx="4" stroke="#4a90e2" stroke-width="2.5"/>
                    <path d="M16 14 L18 8 L30 8 L32 14" stroke="#4a90e2" stroke-width="2.5" stroke-linejoin="round"/>
                    <circle cx="24" cy="27" r="7" stroke="#4a90e2" stroke-width="2.5"/>
                  </svg>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件输入（单张） -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      style="display: none"
      @change="onFileSelected"
    />
    <!-- 隐藏的文件输入（多张，用于更多照片） -->
    <input
      ref="multiFileInput"
      type="file"
      accept="image/*"
      multiple
      style="display: none"
      @change="onMultiFileSelected"
    />

    <!-- 图片预览弹窗 -->
    <div v-if="previewVisible" class="preview-mask" @click="previewVisible = false">
      <img :src="previewUrl" class="preview-img" @click.stop />
      <span class="preview-close" @click="previewVisible = false">
        <i class="el-icon-close"></i>
      </span>
    </div>

    <!-- 提交成功弹窗 -->
    <div v-if="showSuccess" class="modal-mask" @click="closeSuccess">
      <div class="success-modal" @click.stop>
        <span class="modal-close" @click="closeSuccess">
          <i class="el-icon-close"></i>
        </span>
        <div class="success-icon">
          <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="32" fill="#52c41a"/>
            <path d="M18 33 L28 43 L46 23" stroke="#fff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <p class="success-text">提车信息提交成功！</p>
        <button class="success-btn" @click="goMyOrders">查看订单</button>
      </div>
    </div>
  </div>
</template>

<script>
import { upOrderImage, orderImage, startServer } from '@/api/order'
import { getLng, getLat, fixImgUrl } from '@/utils/auth'

const MAX_FILE_SIZE = 5 * 1024 * 1024

export default {
  name: 'StartService',
  data() {
    const query = this.$route.query || {}
    return {
      orderId: query.id || '',
      showSuccess: false,
      submitting: false,
      previewVisible: false,
      previewUrl: '',
      currentPhotoItem: null,
      currentMoreIndex: -1,
      formData: {
        start_mileage: '',
        content: ''
      },
      requiredPhotos: [
        { key: '1', label: '提车油表照片', url: '', loading: false },
        { key: '2', label: '提车公里数照片', url: '', loading: false },
        { key: '3', label: '交强险照片', url: '', loading: false },
        { key: '41', label: '车辆钥匙照片', url: '', loading: false }
      ],
      vehiclePhotos: [
        { key: '5', label: '左前', url: '', loading: false },
        { key: '6', label: '右前', url: '', loading: false },
        { key: '7', label: '左后', url: '', loading: false },
        { key: '8', label: '右后', url: '', loading: false },
        { key: '9', label: '正前', url: '', loading: false },
        { key: '10', label: '正后', url: '', loading: false },
        { key: '11', label: '后备箱(备胎)', url: '', loading: false },
        { key: '12', label: '发动机', url: '', loading: false },
        { key: '13', label: '机油', url: '', loading: false },
        { key: '14', label: '冷冻液', url: '', loading: false },
        { key: '42', label: '底盘', url: '', loading: false }
      ],
      morePhotos: [{ key: '15', url: '', loading: false }]
    }
  },
  created() {
    if (this.orderId) {
      this.loadExistingImages()
    }
  },
  methods: {
    async loadExistingImages() {
      try {
        const res = await orderImage({ order_id: this.orderId, status: 2 })
        if (res.code === 0 && res.data) {
          const d = res.data
          this.requiredPhotos.forEach(item => {
            const url = d['pic_path_' + item.key]
            if (url) item.url = fixImgUrl(url)
          })
          this.vehiclePhotos.forEach(item => {
            const url = d['pic_path_' + item.key]
            if (url) item.url = fixImgUrl(url)
          })
          const moreItems = []
          for (let i = 15; i <= 40; i++) {
            const url = d['pic_path_' + i]
            if (url) {
              moreItems.push({ key: String(i), url: fixImgUrl(url), loading: false })
            }
          }
          if (moreItems.length > 0) {
            const nextKey = 15 + moreItems.length
            if (nextKey <= 40) {
              moreItems.push({ key: String(nextKey), url: '', loading: false })
            }
            this.morePhotos = moreItems
          }
        }
      } catch (err) {
        // 忽略，不影响页面使用
      }
    },
    handlePhotoClick(item) {
      if (item.loading) return
      this.currentPhotoItem = item
      this.currentMoreIndex = -1
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },
    handleMorePhotoClick(index) {
      const item = this.morePhotos[index]
      if (item.loading) return
      this.currentPhotoItem = null
      this.currentMoreIndex = index
      this.$refs.multiFileInput.value = ''
      this.$refs.multiFileInput.click()
    },
    async onFileSelected(e) {
      const file = e.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }
      if (file.size > MAX_FILE_SIZE) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }

      const target = this.currentPhotoItem || (this.currentMoreIndex >= 0 ? this.morePhotos[this.currentMoreIndex] : null)
      if (!target) return

      target.loading = true

      const reader = new FileReader()
      reader.onload = (event) => {
        target.url = event.target.result
      }
      reader.readAsDataURL(file)

      try {
        const formData = new FormData()
        formData.append('order_id', this.orderId)
        formData.append('status', 2)
        formData.append('pic_path_num', target.key)
        formData.append('file', file)
        await upOrderImage(formData)
        target.loading = false
        this.$message.success((target.label || '照片') + '上传成功')
        if (this.currentMoreIndex >= 0) {
          const last = this.morePhotos[this.morePhotos.length - 1]
          if (last.url) {
            const nextKey = 15 + this.morePhotos.length
            if (nextKey <= 40) {
              this.morePhotos.push({ key: String(nextKey), url: '', loading: false })
            }
          }
        }
      } catch (err) {
        target.loading = false
        target.url = ''
        this.$message.error(err.message || '上传失败')
      }
    },
    async onMultiFileSelected(e) {
      const files = Array.from(e.target.files)
      if (!files.length) return

      const validFiles = files.filter(f => f.type.startsWith('image/') && f.size <= MAX_FILE_SIZE)
      if (validFiles.length === 0) {
        this.$message.error('请选择有效的图片文件（不超过5MB）')
        return
      }

      // 计算当前已有更多照片中已上传的数量，确定下一个 key
      const uploadedCount = this.morePhotos.filter(p => p.url).length
      let nextKeyNum = 15 + uploadedCount

      // 为每个文件创建一个上传项
      const itemsToUpload = []
      for (let i = 0; i < validFiles.length; i++) {
        if (nextKeyNum > 40) {
          this.$message.warning('最多只能上传26张更多照片（pic_path_15 到 pic_path_40）')
          break
        }
        const file = validFiles[i]
        const newItem = { key: String(nextKeyNum), url: '', loading: true }
        // 找到第一个空位插入，或追加到末尾
        const emptyIndex = this.morePhotos.findIndex(p => !p.url && !p.loading)
        if (emptyIndex >= 0) {
          this.morePhotos.splice(emptyIndex, 1, newItem)
        } else {
          this.morePhotos.push(newItem)
        }
        itemsToUpload.push({ file, item: newItem })
        nextKeyNum++
      }

      // 并发上传所有文件
      const uploadPromises = itemsToUpload.map(({ file, item }) => {
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = (event) => {
            item.url = event.target.result
          }
          reader.readAsDataURL(file)

          const formData = new FormData()
          formData.append('order_id', this.orderId)
          formData.append('status', 2)
          formData.append('pic_path_num', item.key)
          formData.append('file', file)

          upOrderImage(formData)
            .then(() => {
              item.loading = false
              resolve({ success: true })
            })
            .catch((err) => {
              item.loading = false
              item.url = ''
              resolve({ success: false, msg: err.message })
            })
        })
      })

      const results = await Promise.all(uploadPromises)
      const successCount = results.filter(r => r.success).length
      const failCount = results.length - successCount

      if (failCount === 0) {
        this.$message.success(`成功上传${successCount}张照片`)
      } else if (successCount === 0) {
        this.$message.error('全部上传失败')
      } else {
        this.$message.warning(`成功${successCount}张，失败${failCount}张`)
      }

      // 确保末尾有一个空位供继续添加
      const last = this.morePhotos[this.morePhotos.length - 1]
      if (last && last.url) {
        const nextKey = 15 + this.morePhotos.length
        if (nextKey <= 40) {
          this.morePhotos.push({ key: String(nextKey), url: '', loading: false })
        }
      }
    },
    removePhoto(item) {
      item.url = ''
      this.$message.info('已删除照片')
    },
    removeMorePhoto(index) {
      this.morePhotos.splice(index, 1)
      if (this.morePhotos.length === 0) {
        this.morePhotos.push({ key: '15', url: '', loading: false })
      }
      this.$message.info('已删除照片')
    },
    previewImage(item) {
      this.previewUrl = item.url
      this.previewVisible = true
    },
    async handleSubmit() {
      if (this.submitting) return

      const missing = this.requiredPhotos.filter(p => !p.url)
      if (missing.length > 0) {
        this.$message.warning('请完成所有必填照片上传（带*项）')
        return
      }
      if (!this.formData.start_mileage) {
        this.$message.warning('请输入提车公里数')
        return
      }

      this.submitting = true
      try {
        const res = await startServer({
          order_id: this.orderId,
          lng: getLng(),
          lat: getLat(),
          content: this.formData.content,
          driver_city: 0,
          start_mileage: this.formData.start_mileage
        })
        if (res.code === 0) {
          this.$router.replace({
            path: '/order-detail',
            query: { id: this.orderId }
          })
        } else {
          this.$message.error(res.msg || '提交失败')
        }
      } catch (err) {
        this.$message.error(err.message || '提交失败')
      } finally {
        this.submitting = false
      }
    },
    closeSuccess() {
      this.showSuccess = false
    },
    goMyOrders() {
      this.showSuccess = false
      this.$router.push('/my-orders')
    }
  }
}
</script>

<style scoped>
.pickup-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

/* 顶部导航栏 */
.nav-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  background: #fff;
  position: relative;
  flex-shrink: 0;
  border-bottom: 1px solid #eee;
}

.nav-back {
  position: absolute;
  left: 12px;
  font-size: 22px;
  color: #333;
  cursor: pointer;
  padding: 4px;
}

.nav-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.nav-submit {
  position: absolute;
  right: 16px;
  font-size: 14px;
  color: #333;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 4px 14px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s;
}

.nav-submit:active {
  background: #f0f0f0;
}

.nav-submit.disabled {
  color: #999;
  border-color: #ddd;
  cursor: not-allowed;
}

/* 内容区 */
.content {
  flex: 1;
  padding: 12px;
}

/* 顶部提示 */
.tip-banner {
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #f5222d;
  line-height: 1.6;
}

/* 区块 */
.section {
  background: #fff;
  border-radius: 10px;
  padding: 16px 12px;
  margin-bottom: 10px;
}

/* 照片网格 */
.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.photo-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.photo-box {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: #f0f5ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  transition: all 0.2s;
}

.photo-box:active {
  transform: scale(0.96);
}

/* 上传中 */
.photo-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #4a90e2;
  font-size: 12px;
}

.photo-loading i {
  font-size: 28px;
}

/* 已上传预览 */
.photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 7px;
}

.photo-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s;
  border-radius: 7px;
}

.photo-overlay i {
  font-size: 24px;
  color: #fff;
}

.photo-box:hover .photo-overlay {
  opacity: 1;
}

/* 删除按钮 */
.photo-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(245, 34, 45, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  transition: transform 0.2s;
}

.photo-delete i {
  font-size: 12px;
  color: #fff;
}

.photo-delete:active {
  transform: scale(0.85);
}

.camera-icon {
  width: 36px;
  height: 36px;
}

.photo-label {
  margin-top: 8px;
  font-size: 12px;
  color: #333;
  text-align: center;
  line-height: 1.3;
}

.required {
  color: #f5222d;
  margin-left: 2px;
}

/* 表单区 */
.form-section {
  background: #fff;
  border-radius: 10px;
  padding: 0 16px;
  margin-bottom: 10px;
}

.form-item {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
}

.form-item:last-child {
  border-bottom: none;
}

.form-label {
  font-size: 14px;
  color: #333;
  flex-shrink: 0;
  width: 130px;
}

.form-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.form-input {
  flex: 1;
  border: none;
  outline: none;
  text-align: right;
  font-size: 14px;
  color: #333;
  background: transparent;
}

.form-input::placeholder {
  color: #bbb;
}

.input-suffix {
  font-size: 14px;
  color: #999;
  margin-left: 4px;
}

.form-item-textarea {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.form-textarea {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  color: #333;
  outline: none;
  resize: none;
  font-family: inherit;
  box-sizing: border-box;
}

.form-textarea::placeholder {
  color: #bbb;
}

/* 单选按钮 */
.radio-group {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

.radio-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #d9d9d9;
  background: #fff;
  position: relative;
  transition: all 0.2s;
}

.radio-item.active .radio-dot {
  border-color: #4a90e2;
  background: #4a90e2;
}

.radio-item.active .radio-dot::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
}

.radio-item.active {
  color: #4a90e2;
}

/* 更多照片 */
.more-photos {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.more-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.more-tip {
  font-size: 13px;
  color: #f5222d;
  margin-left: 4px;
}

.more-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.more-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* 图片预览弹窗 */
.preview-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.preview-img {
  max-width: 90%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
}

.preview-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.preview-close i {
  font-size: 22px;
  color: #fff;
}

/* 弹窗 */
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.success-modal {
  position: relative;
  width: 80%;
  max-width: 300px;
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px 24px;
  text-align: center;
  animation: modal-in 0.3s ease;
}

@keyframes modal-in {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
}

.modal-close i {
  font-size: 18px;
}

.success-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 20px;
}

.success-text {
  font-size: 16px;
  color: #333;
  font-weight: 500;
  margin-bottom: 16px;
}

.success-order {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 20px;
}

.success-order i {
  color: #4a90e2;
  font-size: 16px;
}

.success-btn {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 22px;
  background: #4a90e2;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.success-btn:active {
  opacity: 0.85;
}
</style>
